(() => {
  'use strict';

  const STORAGE_KEY = 'c_splash_seen';
  const SEEN_CLASS = 'c_splash-seen';

  // 再生開始後のバッファリングを見込んだ猶予。動画尺 + この時間で強制終了する
  const VIDEO_SAFETY_MARGIN = 4000;

  const SELECTOR = {
    splash: '.c_splash',
    media: '.c_splash__media',
    progressBar: '.c_splash__progress-bar',
    progress: '.c_splash__progress',
    skip: '[data-action="skip"]',
    video: '.c_splash__video',
    videoPc: '.c_splash__video--pc',
    videoSp: '.c_splash__video--sp',
    fallback: '.c_splash__fallback',
    slideshow: 'slide-show',
  };

  const CLASS_NAME = {
    hiding: 'is-hiding',
    fallback: 'is-fallback',
    blocked: 'is-blocked',
  };

  // 次回訪問時にインライン同期スクリプトが SEEN_CLASS を付与するため、ここではフラグ保存のみ行う
  function markSeen() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      // Private mode 等で sessionStorage が使えない場合は無視
    }
  }

  function showFallback(splashEl, activeVideo) {
    const fallback = splashEl.querySelector(SELECTOR.fallback);
    if (!fallback) return;

    if (activeVideo) {
      activeVideo.pause();
      activeVideo.hidden = true;
    }

    fallback.hidden = false;
    splashEl.classList.add(CLASS_NAME.fallback);
  }

  function setupVideo(splashEl, onUnplayable) {
    const videoPc = splashEl.querySelector(SELECTOR.videoPc);
    const videoSp = splashEl.querySelector(SELECTOR.videoSp);

    // PC/SP 両方ある場合は非表示側を除去し、同時デコードを防ぐ
    if (videoPc && videoSp) {
      const isSp = window.matchMedia('(max-width: 740px)').matches;
      (isSp ? videoPc : videoSp).remove();
    }

    const activeVideo = splashEl.querySelector(SELECTOR.video);
    if (!activeVideo) return null;

    // 読み込み・デコードの失敗。動画は再生できないためフォールバック画像へ差し替える
    const handleFailure = () => {
      console.error('[c_splash]', 'Video playback failed, showing fallback image');
      showFallback(splashEl, activeVideo);
      if (typeof onUnplayable === 'function') onUnplayable();
    };

    // 自動再生の拒否（iOS の低電力モード、バックグラウンドタブ等）。動画自体は
    // 再生可能なのでフォールバックへ差し替えず、ポスター + 再生ボタンのままタップ待ちにする
    const handleBlocked = () => {
      splashEl.classList.add(CLASS_NAME.blocked);
      splashEl.querySelector(SELECTOR.media)?.addEventListener('click', () => {
        activeVideo.play().catch(() => {});
      });
      if (typeof onUnplayable === 'function') onUnplayable();
    };

    activeVideo.addEventListener('error', handleFailure, { once: true });

    const playPromise = activeVideo.play();
    if (playPromise) {
      // メディア自体のエラーが無い拒否（自動再生ポリシー等）は、再生できる状態として扱う
      playPromise.catch(() => {
        if (activeVideo.error) {
          handleFailure();
        } else {
          handleBlocked();
        }
      });
    }

    return activeVideo;
  }

  // スプラッシュ終了時、スライドショーを先頭スライド（1番上に設定したもの）から開始する
  function resetSlideshowToFirst() {
    const slideShow = document.querySelector(SELECTOR.slideshow);
    if (slideShow && typeof slideShow.select === 'function' && slideShow.selectedIndex > 0) {
      slideShow.select(0, false);
    }
  }

  function initSplash(splashEl) {
    const isEditor = window.Shopify?.designMode === true;

    if (!isEditor && (document.documentElement.classList.contains(SEEN_CLASS) || sessionStorage.getItem(STORAGE_KEY))) {
      splashEl.remove();
      return;
    }

    const displayTime = parseInt(splashEl.dataset.displayTime, 10) * 1000;
    const progressBar = splashEl.querySelector(SELECTOR.progressBar);
    const progressEl = splashEl.querySelector(SELECTOR.progress);
    const skipBtn = splashEl.querySelector(SELECTOR.skip);

    // スクロールバー消失による横ずれ（固定要素の動画がズレる）を防ぐため、表示中は幅を確保
    document.documentElement.style.scrollbarGutter = 'stable';
    document.body.style.overflow = 'hidden';

    let timer = null;
    let ariaInterval = null;
    let rafId = null;
    let dismissed = false;

    /* 経過時間ベースの進捗。画像モードと、動画を再生できなかった場合に使う */
    function startTimeProgress() {
      // 二重起動と、再生位置ベースの進捗との競合を防ぐ
      if (ariaInterval !== null || rafId !== null) return;

      progressBar.style.transitionDuration = `${displayTime}ms`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          progressBar.style.width = '100%';
        });
      });

      const startTime = Date.now();
      const totalSeconds = displayTime / 1000;
      ariaInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        progressEl.setAttribute('aria-valuenow', Math.min(elapsed, totalSeconds).toFixed(1));
      }, 100);
    }

    /* 再生位置ベースの進捗。バッファリング中はバーも止まるので実態と合う */
    function startVideoProgress(video) {
      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return false;

      // 自動再生拒否後にタップで再生された場合、経過時間ベースの更新が残っているので止める
      clearInterval(ariaInterval);
      ariaInterval = null;

      progressEl.setAttribute('aria-valuemax', duration.toFixed(1));
      progressBar.style.transitionDuration = '0ms';

      const step = () => {
        const ratio = Math.min(video.currentTime / duration, 1);
        progressBar.style.width = `${ratio * 100}%`;
        progressEl.setAttribute('aria-valuenow', video.currentTime.toFixed(1));
        rafId = requestAnimationFrame(step);
      };
      step();

      return true;
    }

    function dismiss() {
      if (dismissed) return;
      dismissed = true;

      clearTimeout(timer);
      clearInterval(ariaInterval);
      if (rafId !== null) cancelAnimationFrame(rafId);

      document.body.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';

      if (activeVideo) {
        activeVideo.pause();
      }

      resetSlideshowToFirst();

      if (!isEditor) {
        markSeen();
      }

      splashEl.classList.add(CLASS_NAME.hiding);

      // 進捗バーやスキップボタンの transitionend もバブリングしてくるため、
      // スプラッシュ自身の opacity 完了だけを拾う
      splashEl.addEventListener('transitionend', (event) => {
        if (event.target !== splashEl || event.propertyName !== 'opacity') return;
        splashEl.remove();
      });
    }

    const activeVideo = setupVideo(splashEl, startTimeProgress);

    if (activeVideo) {
      // 再生が始まった時点で、進捗と終了予定を動画の尺基準へ切り替える
      activeVideo.addEventListener(
        'playing',
        () => {
          if (dismissed || !startVideoProgress(activeVideo)) return;

          clearTimeout(timer);
          timer = setTimeout(dismiss, activeVideo.duration * 1000 + VIDEO_SAFETY_MARGIN);
        },
        { once: true }
      );

      // 動画が最後まで再生されたら終了する
      activeVideo.addEventListener('ended', dismiss, { once: true });
    } else {
      startTimeProgress();
    }

    // 再生が始まらない場合の保険
    timer = setTimeout(dismiss, displayTime);

    skipBtn.addEventListener('click', dismiss);
  }

  const splash = document.querySelector(SELECTOR.splash);
  if (splash) initSplash(splash);

  document.addEventListener('shopify:section:load', (event) => {
    const s = event.target.querySelector(SELECTOR.splash);
    if (s) initSplash(s);
  });
})();
